import type { MappedPersonForSelection } from '@/store/persons/person.dto';
import { TaskParticipantType, TaskStatus } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';

export async function syncDailyLogCrewTasks(
  dailyLogId: string,
  dailyLogDate: string,
  previousCrewIds: string[],
  nextCrewIds: string[],
  people: MappedPersonForSelection[],
): Promise<void> {
  const prevSet = new Set(previousCrewIds);
  const nextSet = new Set(nextCrewIds);
  const added = nextCrewIds.filter((id) => !prevSet.has(id));
  const removed = previousCrewIds.filter((id) => !nextSet.has(id));
  if (!added.length && !removed.length) return;

  const logDateStr = dailyLogDate.slice(0, 10);
  const endOfDay = `${logDateStr}T23:59:59.000Z`;
  const marker = `(${dailyLogId})`;
  const peopleMap = new Map(people.map((p) => [p.id, p]));

  const { createTask, getTasksByResponsible, removeTask } = useTasks.getState();

  await Promise.all([
    ...added.map(async (crewId) => {
      try {
        const person = peopleMap.get(crewId);
        const name = person ? `${person.firstName} ${person.lastName}`.trim() : 'Unknown';
        await createTask({
          title: `Fill in and sign the daily log (${logDateStr})`,
          description: `This task comes from daily log ${logDateStr} (${dailyLogId}). Removing the crew member from the entry removes it.`,
          type: 'deadline_based',
          status: TaskStatus.PENDING,
          priority: 'medium',
          visibility: 'private',
          start: endOfDay,
          deadline: endOfDay,
          reminder: endOfDay,
          time_slot: { type: 'day_based', start: endOfDay, end: endOfDay },
          responsible: { person_id: crewId, name, type: TaskParticipantType.CREW },
          location: { name: '', address: '', latitude: 0, longitude: 0, map_url: '' },
        });
      } catch (err) {
        console.error('[syncDailyLogCrewTasks] create task for crew', crewId, err);
      }
    }),
    ...removed.map(async (crewId) => {
      try {
        const tasks = await getTasksByResponsible(crewId);
        await Promise.all(
          tasks
            .filter((t) => typeof t.description === 'string' && t.description.includes(marker))
            .map(async (t) => {
              try {
                await removeTask(t.id);
              } catch (err) {
                console.error('[syncDailyLogCrewTasks] remove task', t.id, err);
              }
            }),
        );
      } catch (err) {
        console.error('[syncDailyLogCrewTasks] list tasks for crew', crewId, err);
      }
    }),
  ]);
}
